<script setup lang="ts">
import { computed, onMounted, reactive,  Ref,  ref } from 'vue';
import vSelect from 'vue-select';
import CModal from '../CModal.vue';
import CInput from '@/components/shared/CInput.vue';
import CAlert from '@/components/shared/CAlert.vue';
import CButton from '@/components/shared/CButton';
import CEmptyState from '@/components/shared/CEmptyState.vue';
import CPagination, { type PageData } from '@/components/shared/CPagination';
import CSwitch, { type CSwitchOption } from '@/components/shared/CSwitch';
import BoxElement, { type BoxData } from '@/components/shared/Box';
import type { ProductGuideSpecs, SpecsTemplateProductDTO, TemplateSpecDetailDTO } from '@/typings/pod/productGuideSpecs';
import { Provider } from '../../utils/PodProvider.enum';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import { getGuideSpecs, getSpecDetails, listTemplateProductSpecs } from '@/api/templateProduct';

const SPEC_MODE = {
    ALL: 'all',
    WITH_GUIDELINES: 'guidelines'
}

const emit = defineEmits<{
    (event: 'import-specs', value: ProductGuideSpecs): void;
    (event: 'close'): void;
}>();

const showLoader = ref(false);

const filters: CSwitchOption[] = [
    {
        label: 'All',
        value: SPEC_MODE.ALL,
    },
    {
        label: 'With guidelines',
        value: SPEC_MODE.WITH_GUIDELINES,
    }
];
const selectedSpecMode = ref(SPEC_MODE.ALL)
const filterByGuidelineActive = computed(() => selectedSpecMode.value == SPEC_MODE.WITH_GUIDELINES);

const pagination = reactive({
    page: 1,
    totalCount: 0,
    pageSize: 8,
    searchString: ''
})
const products = ref<BoxData<SpecsTemplateProductDTO>[]>([]);

const onPageChange = async (data: PageData) => {
    pagination.page = data.page;
    await getProducts(data.from);
};

const getProducts = async (from = 0) => {
    showLoader.value = true;
    selectedProduct.value = undefined;
    const { results, totalCount } = await listTemplateProductSpecs(
        from,
        pagination.pageSize,
        filterByGuidelineActive.value,
        pagination.searchString,
    );

    products.value = results.map((product) => {
        const name = product.name;
        return {
            id: product.id,
            title: name.toLowerCase().includes(Provider.PRINTFUL)
                ? name.split('(Printful)')[0]
                : name.split('(Printify)')[0],
            image: buildCloudFrontUrl(product.thumbnailPath, true),
            object: { ...product, podImageUrl: buildCloudFrontUrl(`app/images_pod/thumbnails/${product.podImageUrl}`, true)}
        };
    });

    pagination.totalCount = totalCount;
    showLoader.value = false;
};

const selectedProduct: Ref<BoxData<SpecsTemplateProductDTO> | undefined> = ref();
const onProductSeleted = (product: BoxData<SpecsTemplateProductDTO>) => {
    selectedProduct.value = {...product}
};

const nextBtnText = computed(() => {
    const text = 'Next'
    if (!selectedProduct.value) return text;

    return selectedProduct.value.object?.hasGuidelines
        ? text
        : 'Confirm'
});

const specDetails = reactive({
    show: false,
    templates: [] as TemplateSpecDetailDTO[],
    printAreas: [] as TemplateSpecDetailDTO['listEps'],
    selectedTemplate: undefined as TemplateSpecDetailDTO | undefined,
    selectedPrintArea: undefined as number | undefined,
});

const onNextHandler = async () => {
    const product = selectedProduct.value?.object
    if (!product) return;

    if (!product.hasGuidelines) {
        await emitProductSpecs(product.id);
        return;
    }

    specDetails.show = true;
    const details = await getSpecDetails(product.id);
    specDetails.templates = [...details];

    if (details.length > 1) return;

    const size = details[0];

    if (size.listEps.length > 1) {
        specDetails.selectedTemplate = details[0];
        specDetails.printAreas = details[0].listEps;
        return;
    }

    await emitProductSpecs(size.id, Number(size.listEps[0].id));
};

const onSizeChange =  () => {
    const template = specDetails.selectedTemplate;
    specDetails.selectedPrintArea = undefined;
    if (!template) return;

    specDetails.selectedPrintArea = Number(template.listEps[0].id);

    specDetails.printAreas = [...template.listEps];
}

const emitProductSpecs = async (templateId?: string, epsId?: number) => {
    if (!templateId?.length) return;
    const podProductSpecs = await getGuideSpecs(templateId, epsId);
    emit('import-specs', podProductSpecs);
    onClose();
}

const onClose = () => {
    clearAll();
    emit('close');
};

const vSelectReducer = (option: Record<string, number>) => option.id;

const clearAll = () => {
    pagination.page = 1;
    pagination.searchString = '';
    selectedProduct.value = undefined;
    resetSpecDetails();
};

const resetSpecDetails = () => {
    specDetails.show = false;
    specDetails.templates = [];
    specDetails.printAreas = [];
    specDetails.selectedTemplate = undefined;
    specDetails.selectedPrintArea = undefined;
}

onMounted(() => {
    getProducts();
});
</script>

<template>
    <c-modal :value="true" size="lg" title="Select a product spec for your design" @closed="onClose">
        <div v-if="!specDetails.show" class="product-list-wrapper">
            <div class="d-flex justify-content-between align-items-center">
                <c-switch v-model="selectedSpecMode" :options="filters" @input="onPageChange({ page: 1, from: 0 })" />

                <c-input
                    v-model="pagination.searchString"
                    style="width: 45%"
                    placeholder="Search products"
                    :disabled="showLoader"
                    :debounce="1000"
                    @debounce="onPageChange({ page: 1, from: 0 })"
                />
            </div>

            <template v-if="products.length">
                <c-alert
                    v-if="!filterByGuidelineActive"
                    text="For those products that do not have guidelines, only the dimensions will be imported."
                />
                <div class="list-container">
                    <div
                        v-for="product of products"
                        :key="product.id"
                        class="product-card"
                        :class="{ selected: selectedProduct?.id == product.id }"
                        @click="onProductSeleted(product)"
                    >
                        <box-element :box-data="product">
                            <div class="pod-product-badge">
                                <img :src="product.object?.podImageUrl" :alt="`${product.object?.pod || 'pod'} logo`" />
                            </div>
                        </box-element>
                    </div>
                </div>
                <c-pagination
                    v-model="pagination.page"
                    class="mx-auto mt-3"
                    size="md"
                    :disabled="showLoader"
                    :total-count="pagination.totalCount"
                    :page-count="pagination.pageSize"
                    @page-change="onPageChange"
                />
            </template>
            <c-empty-state v-else class="p-5" />
        </div>
        <template v-else>
            <p class="font-weight-bold">Select the specs you want to use for your design.</p>
            <div class="d-flex">
                <div class="product-preview">
                    <img class="product-thumb" :src="selectedProduct?.image" />
                    <div class="pod-product-badge">
                        <img :src="selectedProduct?.object?.podImageUrl" />
                    </div>
                    <span class="font-weight-bold">
                        {{ selectedProduct?.title }}
                    </span>
                </div>
                <div class="flex-grow-1 ml-2">
                    <template v-if="specDetails.templates.length > 1">
                        <label class="mt-2">Size</label>
                        <v-select
                            v-model="specDetails.selectedTemplate"
                            label="name"
                            placeholder="Select"
                            append-to-body
                            :options="specDetails.templates"
                            @option:selected="onSizeChange"
                        />
                    </template>
                    <template v-if="specDetails.printAreas.length > 1">
                        <label class="mt-2">Print area guideline</label>
                        <v-select
                            v-model="specDetails.selectedPrintArea"
                            label="value"
                            placeholder="Select"
                            append-to-body
                            :searchable="false"
                            :reduce="vSelectReducer"
                            :options="specDetails.printAreas"
                        />
                    </template>
                </div>
            </div>
        </template>
        <template #modal-footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <c-button variant="secondary" text="Cancel" @click="onClose" />
                <c-button
                    v-if="!specDetails.show"
                    variant="primary"
                    :disabled="!Boolean(selectedProduct)"
                    :text="nextBtnText"
                    @click="onNextHandler"
                />
                <div v-else>
                    <c-button variant="outline-primary" text="Back" @click="resetSpecDetails" />
                    <c-button
                        variant="primary"
                        text="Confirm"
                        :disabled="!specDetails.selectedPrintArea"
                        @click="emitProductSpecs(specDetails.selectedTemplate?.id, specDetails.selectedPrintArea)"
                    />
                </div>
            </div>
        </template>
    </c-modal>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.product-list-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    .list-container {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.625rem;

        .product-card {
            cursor: pointer;
            padding: 0.5rem 0.625rem;
            border-radius: 0.5rem;
            border: 1px solid get-color('gray-lightest');
            &.selected {
                border-width: 2px;
                border-color: get-color('purple');
            }

            :deep() {
                .box-el-wrapper {
                    .block {
                        border: unset;
                    }
                    .main-text {
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                    }
                }
            }
        }
    }
}

.product-preview {
    width: 15.625rem;
    .product-thumb {
        width: 100%;
    }
}

.pod-product-badge {
    height: 1.25rem;
    margin-bottom: 5px;
    img {
        max-height: 100%;
        object-fit: contain;
    }
}
</style>
